import * as THREE from "three";
import debug from "debug";

const log = debug("app:collision");

/**
 * Class responsible for detecting collisions between groups in a 3D scene.
 * @category Experience
 * @subcategory ObjectManagement
 */
class DetectCollision {
  /**
   * Constructs a DetectCollision instance.
   * @param {Object} experience - The main application experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   * @param {Object} experience.eventHub - Event hub for managing custom events.
   */
  constructor(experience) {
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;
    this.collisionDetectionEnabled = true; // Default to enabled
    this.setupListeners();
  }

  cleanup() {
    console.log("Cleaning up DetectCollision...");

    // Remove event listeners
    this.eventHub.off("3d-enabled", this.checkCollision);
    this.eventHub.off("xmd", this.checkCollision);

    // Clear stored references
    this.scene = null;
    this.eventHub = null;

    console.log("DetectCollision cleanup complete.");
  }

  /**
   * Sets up event listeners for collision detection.
   */
  setupListeners() {
    this.eventHub.on("3d-enabled", ({ enabled }) => {
      this.collisionDetectionEnabled = !enabled; // Disable if 3D is on
    });

    this.eventHub.on("xmd", () => {
      if (this.collisionDetectionEnabled) this.checkCollision();
    });
  }

  /**
   * Checks for collisions between groups in the scene based on their bounding boxes.
   * Shrinks bounding boxes by a specified factor before detecting collisions.
   */
  checkCollision() {
    this.scene.updateMatrixWorld(true);
    const groups = this.scene.children.filter(
      (child) =>
        child.isGroup &&
        child.name === "XMO" &&
        child.userData.layer != "33" &&
        child.userData.layer != "50" &&
        child.userData.layer != "70",
    );
    const collidingObjects = new Set();
    const shrinkFactor = 0.0001; // Adjust this value as needed

    groups.forEach((group) => {
      group.children.forEach((object) => {
        if (object.name === "bBox") {
          log("Checking object:", object);

          // Get the original bounding box
          const bboxObject = new THREE.Box3().setFromObject(object);
          const size = bboxObject.getSize(new THREE.Vector3());

          // Shrink the bounding box by a factor
          const min = bboxObject.min
            .clone()
            .add(size.clone().multiplyScalar(shrinkFactor));
          const max = bboxObject.max
            .clone()
            .add(size.clone().multiplyScalar(-shrinkFactor));
          const shrunkBBoxObject = new THREE.Box3(min, max);

          groups.forEach((otherGroup) => {
            if (group !== otherGroup) {
              otherGroup.children.forEach((otherObject) => {
                if (otherObject.name === "bBox") {
                  const bboxOther = new THREE.Box3().setFromObject(otherObject);
                  const otherSize = bboxOther.getSize(new THREE.Vector3());

                  // Shrink the other bounding box by a factor
                  const otherMin = bboxOther.min
                    .clone()
                    .add(otherSize.clone().multiplyScalar(shrinkFactor));
                  const otherMax = bboxOther.max
                    .clone()
                    .add(otherSize.clone().multiplyScalar(-shrinkFactor));
                  const shrunkBBoxOther = new THREE.Box3(otherMin, otherMax);

                  if (shrunkBBoxObject.intersectsBox(shrunkBBoxOther)) {
                    log("Collision detected between", group, "and", otherGroup);
                    collidingObjects.add(group);
                    collidingObjects.add(otherGroup);
                  }
                }
              });
            }
          });
        }
      });
    });

    // Emit an event with colliding objects
    log("Colliding objects:", Array.from(collidingObjects));
    this.eventHub.emit("collisionUpdated", {
      collidingObjects: Array.from(collidingObjects),
    });
  }
}

export default DetectCollision;
